import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, MagnifyingGlass, List, X, SealCheck, Armchair, Phone, WhatsappLogo } from "@phosphor-icons/react";

export const Header = ({ minimal = false }) => {
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  return (
    <header className="sticky top-0 z-40 w-full border-b border-slate-200 bg-white/85 backdrop-blur-xl">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
        <Link to="/" className="flex items-center gap-2.5" data-testid="header-logo">
          <div className="w-9 h-9 rounded-lg bg-blue-600 flex items-center justify-center">
            <ShieldCheck size={22} weight="fill" color="white" />
          </div>
          <div className="leading-tight">
            <div className="font-bold text-slate-900 text-base">Servus Autoglas</div>
            <div className="hidden sm:block text-[10px] uppercase tracking-widest text-slate-500 -mt-0.5">Mannheim · Ludwigshafen · München</div>
          </div>
        </Link>
        {!minimal && (
          <div className="hidden md:flex items-center gap-3">
            <Button
              variant="ghost"
              className="h-10 text-pink-600 hover:text-pink-700 hover:bg-pink-50 font-semibold"
              onClick={() => navigate("/scheibentoenung")}
              data-testid="header-tint-link"
            >
              <SealCheck size={18} weight="fill" className="mr-1.5" />
              Scheibentönung
            </Button>
            <Button
              variant="ghost"
              className="h-10 text-[#249143] hover:text-[#1c7436] hover:bg-[#EAF8ED] font-semibold"
              onClick={() => navigate("/sattlerei")}
              data-testid="header-sattlerei-link"
            >
              <Armchair size={18} weight="fill" className="mr-1.5" />
              Sattlerei
            </Button>
            <Button
              variant="ghost"
              className="h-10 text-slate-600 hover:text-slate-900"
              onClick={() => navigate("/status")}
              data-testid="header-track-button"
            >
              <MagnifyingGlass size={18} className="mr-2" />
              Status verfolgen
            </Button>
            <Button
              className="h-10 bg-blue-600 hover:bg-blue-700 text-white"
              onClick={() => navigate("/schaden-melden")}
              data-testid="header-report-button"
            >
              Schaden melden
            </Button>
          </div>
        )}
        <div className="md:hidden flex items-center gap-2">
          <Button
            className="h-10 bg-blue-600 hover:bg-blue-700 text-white px-4"
            onClick={() => navigate("/schaden-melden")}
            data-testid="header-report-button-mobile"
          >
            Schaden melden
          </Button>
          {!minimal && (
            <button
              onClick={() => setOpen(!open)}
              className="w-10 h-10 flex items-center justify-center rounded-lg border border-slate-200 text-slate-700 hover:bg-slate-50"
              aria-label="Menü öffnen"
              data-testid="header-mobile-menu-button"
            >
              {open ? <X size={20} /> : <List size={20} />}
            </button>
          )}
        </div>
      </div>
      {!minimal && open && (
        <nav className="md:hidden border-t border-slate-200 bg-white px-4 py-3 space-y-1" data-testid="header-mobile-menu">
          <Link
            to="/scheibentoenung"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-pink-50 text-pink-700 font-semibold"
            data-testid="header-tint-link-mobile"
          >
            <SealCheck size={18} weight="fill" />
            Scheibentönung
            <span className="ml-auto text-[10px] font-bold uppercase tracking-widest bg-pink-600 text-white px-2 py-0.5 rounded-full">Neu</span>
          </Link>
          <Link
            to="/sattlerei"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-3 rounded-xl bg-[#EAF8ED] text-[#249143] font-semibold"
            data-testid="header-sattlerei-link-mobile"
          >
            <Armchair size={18} weight="fill" />
            Sattlerei
            <span className="ml-auto text-[10px] font-bold uppercase tracking-widest bg-[#55C96B] text-white px-2 py-0.5 rounded-full">Neu</span>
          </Link>
          <Link
            to="/status"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-3 rounded-xl text-slate-700 font-medium hover:bg-slate-50"
            data-testid="header-track-link-mobile"
          >
            <MagnifyingGlass size={18} />
            Status verfolgen
          </Link>
          <Link
            to="/leistungen"
            onClick={() => setOpen(false)}
            className="flex items-center gap-2.5 px-3 py-3 rounded-xl text-slate-700 font-medium hover:bg-slate-50"
            data-testid="header-services-link-mobile"
          >
            <ShieldCheck size={18} />
            Leistungen
          </Link>
          <div className="grid grid-cols-2 gap-2 pt-2 pb-1">
            <a
              href="tel:+49621527429"
              className="flex items-center justify-center gap-2 h-12 rounded-xl bg-slate-900 text-white font-semibold"
              data-testid="header-call-link-mobile"
            >
              <Phone size={18} weight="fill" />
              Anrufen
            </a>
            <a
              href="https://wa.me/4915222717582"
              target="_blank"
              rel="noreferrer"
              className="flex items-center justify-center gap-2 h-12 rounded-xl bg-emerald-600 text-white font-semibold"
              data-testid="header-whatsapp-link-mobile"
            >
              <WhatsappLogo size={18} weight="fill" />
              WhatsApp
            </a>
          </div>
        </nav>
      )}
    </header>
  );
};

export default Header;
